package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.key
import com.appsyde.shared.modules.account.domain.usecase.CompleteOnboardingUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ListTicketsUseCase
import com.appsyde.shared.app.presentation.team.TeamViewModel
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import com.appsyde.shared.presentation.style.style.AppStylesWizard
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*
import org.koin.core.context.GlobalContext

private const val TOTAL_STEPS = 2

/**
 * Wizard onboarding : header app (logo + caption "Setup 1/2"), une card premium avec
 * header dans la card (titre + sub + stepper), body (subcards), footer sticky (Passer / Continuer).
 */
@Composable
fun OnboardingWizardScreen(onComplete: () -> Unit) {
    var currentStep by remember { mutableStateOf(0) }
    val teamViewModel = remember { GlobalContext.get().get<TeamViewModel>() }
    val teamState by teamViewModel.state.collectAsState()
    val completeOnboardingUseCase = remember { GlobalContext.get().get<CompleteOnboardingUseCase>() }
    val listTicketsUseCase = remember { GlobalContext.get().get<ListTicketsUseCase>() }
    val scope = rememberCoroutineScope()
    var completing by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        teamViewModel.load()
    }

    LaunchedEffect(Unit) {
        listTicketsUseCase(limit = 100)
    }

    Style(AppStylesAnimations)
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesWizard)
    Style(AppStylesAccount)

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {

        // Topbar : logo + caption micro "Setup 1/2" (pas d'onglets, pas de Mon compte)
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesHeader.wizardHeaderInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                        Span(attrs = { classes(AppStylesHeader.brandSubtitle) }) { Text("Configuration") }
                    }
                }
                Span(attrs = { classes(AppStylesWizard.wizardCaption) }) {
                    Text("Setup ${currentStep + 1}/$TOTAL_STEPS")
                }
                Span() {}
            }
        }

        Div(attrs = { classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap, AppStylesLayout.wizardPageWrap) }) {
            Div(attrs = { classes(AppStylesLayout.wizardContentWrapCentered) }) {
                Div(attrs = { classes(AppStylesLayout.wizardCardWrap) }) {

                    Div(attrs = { classes(AppStylesWizard.wizardCard) }) {
                        key(currentStep) {
                            Div(attrs = { classes(AppStylesWizard.wizardCardInner) }) {
                                when (currentStep) {
                                    0 -> {
                                        // Hero : statut top right, icon, titre, sub, stepper minimal
                                        Div(attrs = { classes(AppStylesWizard.wizardCardHeader) }) {
                                            Div(attrs = { classes(AppStylesWizard.wizardStatusLine) }) {
                                                Text("✔ ")
                                                Text("Boîte connectée")
                                            }
                                            Span(attrs = { classes(AppStylesWizard.wizardHeroIcon) }) {
                                                Text(
                                                    "👥"
                                                )
                                            }
                                            Div(attrs = { classes(AppStylesWizard.wizardTitle) }) {
                                                Text("Invitez votre équipe")
                                            }
                                            Div(attrs = { classes(AppStylesWizard.wizardSubWrap) }) {
                                                Div(attrs = { classes(AppStylesWizard.wizardSub) }) {
                                                    Text("Ajoutez vos agents pour collaborer sur les tickets.")
                                                }
                                                Div(attrs = { classes(AppStylesWizard.wizardSub) }) {
                                                    Text("Vous pourrez toujours modifier l'équipe plus tard.")
                                                }
                                            }
                                            Div(attrs = { classes(AppStylesWizard.wizardStepperWrap) }) {
                                                Div(attrs = { classes(AppStylesWizard.wizardStepper) }) {
                                                    Div(attrs = {
                                                        classes(
                                                            AppStylesWizard.wizardStepperDot,
                                                            AppStylesWizard.wizardStepperDotActive
                                                        )
                                                    }) {}
                                                    Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                                        Text(
                                                            "Configuration"
                                                        )
                                                    }
                                                    Div(attrs = { classes(AppStylesWizard.wizardStepperLine) }) {
                                                        Div(attrs = {
                                                            classes(AppStylesWizard.wizardStepperLineProgress)
                                                            attr("style", "width: 50%")
                                                        }) {}
                                                    }
                                                    Div(attrs = { classes(AppStylesWizard.wizardStepperDot) }) {}
                                                    Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                                        Text(
                                                            "Terminé"
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardCardBody) }) {
                                            TeamInviteSection(
                                                teamViewModel = teamViewModel,
                                                teamState = teamState,
                                                wizardMode = true
                                            )
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardCardFooter) }) {
                                            Button(attrs = {
                                                classes(AppStylesWizard.wizardBtnSecondary)
                                                attr("type", "button")
                                                onClick { currentStep = 1 }
                                            }) { Text("Passer") }
                                            Button(attrs = {
                                                classes(AppStylesWizard.wizardBtnPrimary)
                                                attr("type", "button")
                                                onClick { currentStep = 1 }
                                            }) { Text("Continuer") }
                                        }
                                    }

                                    1 -> {
                                        Div(attrs = { classes(AppStylesWizard.wizardCardHeader) }) {
                                            Div(attrs = { classes(AppStylesWizard.wizardStepperWrap) }) {
                                                Div(attrs = { classes(AppStylesWizard.wizardStepper) }) {
                                                    Div(attrs = {
                                                        classes(
                                                            AppStylesWizard.wizardStepperDot,
                                                            AppStylesWizard.wizardStepperDotActive
                                                        )
                                                    }) {}
                                                    Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                                        Text("Configuration")
                                                    }
                                                    Div(attrs = { classes(AppStylesWizard.wizardStepperLine) }) {
                                                        Div(attrs = {
                                                            classes(AppStylesWizard.wizardStepperLineProgress)
                                                            attr("style", "width: 100%")
                                                        }) {}
                                                    }
                                                    Div(attrs = {
                                                        classes(
                                                            AppStylesWizard.wizardStepperDot,
                                                            AppStylesWizard.wizardStepperDotActive
                                                        )
                                                    }) {}
                                                    Span(attrs = { classes(AppStylesWizard.wizardStepperLabel) }) {
                                                        Text("Terminé")
                                                    }
                                                }
                                            }
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardCardBody, AppStylesWizard.wizardCardBodyCentered, AppStylesWizard.wizardReadyBody) }) {
                                            Div(attrs = { classes(AppStylesWizard.wizardReadyBlock) }) {
                                                Div(attrs = { classes(AppStylesWizard.wizardReadyIconWrap) }) {
                                                    Span(attrs = { classes(AppStylesWizard.wizardReadyIcon) }) {
                                                        Text("✔")
                                                    }
                                                }
                                                Div(attrs = { classes(AppStylesWizard.wizardReadyTitle, AppStylesWizard.wizardReadyTitleSpacing) }) {
                                                    Text("Votre boîte est prête")
                                                }
                                                Div(attrs = { classes(AppStylesWizard.wizardReadySub, AppStylesWizard.wizardReadySubWide) }) {
                                                    Text("Les emails Gmail sont maintenant connectés et vous pouvez commencer à répondre.")
                                                }
                                                Div(attrs = { classes(AppStylesWizard.wizardReadySub, AppStylesWizard.wizardReadySubWide) }) {
                                                    Text("AppSyde affiche vos tickets et prépare les réponses.")
                                                }
                                                Div(attrs = { classes(AppStylesWizard.wizardReadyChecklist) }) {
                                                    Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                                        Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) { Text("✓") }
                                                        Text("Gmail connecté")
                                                    }
                                                    Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                                        Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) { Text("✓") }
                                                        Text("Équipe configurée")
                                                    }
                                                    Div(attrs = { classes(AppStylesWizard.wizardReadyCheckItem) }) {
                                                        Span(attrs = { classes(AppStylesWizard.wizardReadyCheckMark) }) { Text("✓") }
                                                        Text("AppSyde prêt")
                                                    }
                                                }
                                            }
                                        }
                                        Div(attrs = { classes(AppStylesWizard.wizardCardFooterCenter, AppStylesWizard.wizardReadyFooter) }) {
                                            Button(attrs = {
                                                classes(AppStylesWizard.wizardBtnPrimaryWide)
                                                attr("type", "button")
                                                if (completing) attr("disabled", "true")
                                                onClick {
                                                    completing = true
                                                    scope.launch {
                                                        try {
                                                            if (completeOnboardingUseCase()) {
                                                                onComplete()
                                                            }
                                                        } finally {
                                                            completing = false
                                                        }
                                                    }
                                                }
                                            }) {
                                                Text(if (completing) "Chargement…" else "Aller à la boîte")
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
